/**
 * Council Trail v0.1 Item 8 — ProposalReviewDialog.
 *
 * Modal listing pending proposals with per-row Approve / Dismiss buttons.
 * Calls back through `resolve` (from useCouncilProposals) — buttons
 * disabled per-row while a resolve is in flight to prevent double-fire.
 *
 * On 409 (raced — already resolved by another tab), the hook surfaces
 * the error via the thrown promise. The dialog catches it, shows an
 * inline toast-line, and lets the parent's refresh land.
 *
 * For v0.1 MVP this is intentionally minimal: each row shows the kind,
 * proposer (short id), rationale, and the kind-specific summary line.
 * Future polish: persona-name resolution + a per-kind preview component
 * (e.g., propose_fork's content preview).
 */
import React, { useContext, useEffect, useState } from 'react';

import { CouncilContext } from '../../contexts/CouncilContext';
import { useConversation } from '../../contexts/ConversationContext';
import type { ProposalRow } from '../../types/council';

export interface ProposalReviewDialogProps {
  open: boolean;
  onClose: () => void;
  proposals: ProposalRow[];
  resolve: (id: string, decision: 'approve' | 'dismiss', note?: string) => Promise<void>;
}

export function ProposalReviewDialog({
  open,
  onClose,
  proposals,
  resolve,
}: ProposalReviewDialogProps) {
  // Auto-close when the dialog drains to empty AFTER it was open with
  // at least one row. Avoids the "empty modal lingers awkwardly" UX —
  // operator approves the last row, dialog dismisses itself. Skip the
  // close when the dialog opened empty in the first place (operator
  // clicked a stale chip).
  const [hadAtLeastOne, setHadAtLeastOne] = useState(false);
  useEffect(() => {
    if (open && proposals.length > 0) setHadAtLeastOne(true);
    if (!open) setHadAtLeastOne(false);
  }, [open, proposals.length]);
  useEffect(() => {
    if (open && hadAtLeastOne && proposals.length === 0) {
      const t = window.setTimeout(() => onClose(), 350);
      return () => window.clearTimeout(t);
    }
    return;
  }, [open, hadAtLeastOne, proposals.length, onClose]);

  // Bulk-approve state. Approves are sequential (not parallel) so the
  // backend's approve-time tail retargeting can chain each new spine
  // extension off the previous one — parallel approves would land all
  // N at the same snapshot tail and pile as a sibling fan (see v0.1
  // known limitation #3 in the closeout journal).
  const { reloadMessages } = useConversation();
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const onApproveAll = async () => {
    if (bulkBusy || proposals.length === 0) return;
    const ids = proposals.map((p) => p.id);
    setBulkBusy(true);
    setBulkError(null);
    for (let i = 0; i < ids.length; i++) {
      setBulkProgress({ current: i + 1, total: ids.length });
      try {
        await resolve(ids[i], 'approve');
      } catch (e) {
        setBulkError((e as Error).message ?? `Failed at ${i + 1}/${ids.length}`);
        break;
      }
    }
    try {
      await reloadMessages();
    } catch {
      // reload errors are non-fatal — the next mount will refresh.
    }
    setBulkBusy(false);
    setBulkProgress(null);
  };

  if (!open) return null;
  return (
    <div
      data-testid="proposal-review-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Pending proposals"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[1px]"
      onClick={onClose}
    >
      <div
        className="relative max-w-2xl w-[min(640px,90vw)] max-h-[80vh] overflow-y-auto
                   rounded-xl shadow-2xl bg-white dark:bg-gray-900
                   border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-5 py-3 flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Pending proposals ({proposals.length})
          </h2>
          <div className="flex items-center gap-2">
            {proposals.length > 1 && (
              <button
                type="button"
                data-testid="proposal-review-approve-all"
                onClick={onApproveAll}
                disabled={bulkBusy}
                className={`text-xs px-2.5 py-1 rounded font-medium
                           bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-500
                           text-white
                           ${bulkBusy ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {bulkBusy && bulkProgress
                  ? `Approving ${bulkProgress.current} of ${bulkProgress.total}…`
                  : `Approve all (${proposals.length})`}
              </button>
            )}
            <button
              type="button"
              data-testid="proposal-review-close"
              onClick={onClose}
              aria-label="Close proposals dialog"
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-lg leading-none px-1"
            >
              ×
            </button>
          </div>
        </div>
        {bulkError && (
          <p
            data-testid="proposal-review-bulk-error"
            className="px-5 py-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800"
          >
            {bulkError}
          </p>
        )}
        <div className="p-4 space-y-3">
          {proposals.length === 0 ? (
            <p
              data-testid="proposal-review-empty"
              className="text-sm text-gray-500 dark:text-gray-400"
            >
              No pending proposals.
            </p>
          ) : (
            proposals.map((p) => (
              <ProposalRowCard
                key={p.id}
                proposal={p}
                resolve={resolve}
                disabled={bulkBusy}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const ProposalRowCard: React.FC<{
  proposal: ProposalRow;
  resolve: (id: string, decision: 'approve' | 'dismiss', note?: string) => Promise<void>;
  disabled?: boolean;
}> = ({ proposal, resolve, disabled = false }) => {
  const [busy, setBusy] = useState<'approve' | 'dismiss' | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const councilCtx = useContext(CouncilContext);
  // After approve, the backend writes a new spine extension into
  // session.data — but ConversationContext won't notice without an
  // explicit reload. Pull reloadMessages so the DAG paints the new
  // peer-tinted node immediately.
  const { reloadMessages } = useConversation();
  const proposer = councilCtx?.state?.personasById?.[proposal.proposed_by_persona_id];
  // Lazy-fetch fallback for when CouncilContext.personasById hasn't hydrated
  // yet (or doesn't include this proposer — e.g., dismissed-then-reproposing
  // peer). One-shot per row; silent failure falls back to the short-id slug.
  const [fetchedName, setFetchedName] = useState<string | null>(null);
  useEffect(() => {
    if (proposer?.name) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `/api/personas/${proposal.proposed_by_persona_id}`,
          { credentials: 'same-origin' },
        );
        if (!r.ok) return;
        const p = await r.json();
        if (!cancelled && p?.name) setFetchedName(p.name as string);
      } catch {
        // intentional silence — fallback slug renders on miss
      }
    })();
    return () => { cancelled = true; };
  }, [proposer, proposal.proposed_by_persona_id]);
  const proposerName =
    proposer?.name
    ?? fetchedName
    ?? `peer ${proposal.proposed_by_persona_id.slice(0, 8)}`;

  const onResolve = async (decision: 'approve' | 'dismiss') => {
    setBusy(decision);
    setLocalError(null);
    try {
      await resolve(proposal.id, decision);
      if (decision === 'approve') {
        await reloadMessages();
      }
    } catch (e) {
      setLocalError((e as Error).message ?? 'Failed to resolve');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid={`proposal-row-${proposal.id}`}
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3 text-sm"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xs uppercase tracking-wide font-semibold text-violet-700 dark:text-violet-300">
          {proposal.kind}
        </span>
        <span className="text-gray-400 dark:text-gray-500">·</span>
        <span className="font-medium text-gray-800 dark:text-gray-100 truncate">
          {proposerName}
        </span>
      </div>
      <p
        data-testid={`proposal-row-${proposal.id}-rationale`}
        className="text-gray-700 dark:text-gray-300 mb-2"
      >
        {proposal.rationale}
      </p>
      {proposal.kind === 'propose_fork' && typeof proposal.target?.content === 'string' && (
        <pre
          data-testid={`proposal-row-${proposal.id}-preview`}
          className="text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700
                     rounded p-2 mb-2 max-h-32 overflow-y-auto whitespace-pre-wrap font-mono text-gray-700 dark:text-gray-300"
        >
          {(proposal.target.content as string).slice(0, 600)}
          {(proposal.target.content as string).length > 600 && '…'}
        </pre>
      )}
      {proposal.kind === 'propose_invite' && (
        <div
          data-testid={`proposal-row-${proposal.id}-invite`}
          className="text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2 mb-2"
        >
          <div className="mb-1">
            <span className="text-gray-500 dark:text-gray-400">invite </span>
            <span className="font-medium text-gray-800 dark:text-gray-100">
              {(proposal.target as any)?.invitee_persona_id
                ? `peer ${String((proposal.target as any).invitee_persona_id).slice(0, 8)}`
                : 'a peer'}
            </span>
            <span className="text-gray-500 dark:text-gray-400"> onto the trail at turn </span>
            <span className="font-mono">{String((proposal.target as any)?.from_turn_id ?? '?').slice(0, 8)}</span>
          </div>
          <div className="text-gray-700 dark:text-gray-300">
            <span className="text-gray-500 dark:text-gray-400">task: </span>
            {String((proposal.target as any)?.task ?? '')}
          </div>
        </div>
      )}
      {proposal.kind === 'propose_merge' && (
        <div
          data-testid={`proposal-row-${proposal.id}-merge`}
          className="text-xs bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-2 mb-2"
        >
          <div className="mb-1">
            <span className="text-gray-500 dark:text-gray-400">fold the camp </span>
            <span className="font-mono">{String((proposal.target as any)?.camp_id ?? '?').slice(0, 12)}</span>
            <span className="text-gray-500 dark:text-gray-400"> back into the main trail</span>
          </div>
          {(proposal.target as any)?.note && (
            <div className="text-gray-700 dark:text-gray-300">
              <span className="text-gray-500 dark:text-gray-400">note: </span>
              {String((proposal.target as any).note)}
            </div>
          )}
        </div>
      )}
      {localError && (
        <p
          data-testid={`proposal-row-${proposal.id}-error`}
          className="text-xs text-red-600 dark:text-red-400 mb-2"
        >
          {localError}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`proposal-row-${proposal.id}-approve`}
          disabled={busy !== null || disabled}
          onClick={() => onResolve('approve')}
          className={`text-xs px-2.5 py-1 rounded font-medium
                     bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-500
                     text-white
                     ${busy || disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {busy === 'approve' ? 'Approving…' : 'Approve'}
        </button>
        <button
          type="button"
          data-testid={`proposal-row-${proposal.id}-dismiss`}
          disabled={busy !== null || disabled}
          onClick={() => onResolve('dismiss')}
          className={`text-xs px-2.5 py-1 rounded
                     border border-gray-300 dark:border-gray-600
                     text-gray-700 dark:text-gray-200
                     hover:bg-gray-100 dark:hover:bg-gray-700
                     ${busy || disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
};
